import type { SVGProps } from "react";

export type AIProvider =
  | "anthropic"
  | "openai"
  | "google"
  | "mistral"
  | "deepseek"
  | "perplexity"
  | "cohere"
  | "moonshot";

type Props = {
  provider: AIProvider;
  size?: number;
} & Omit<SVGProps<SVGSVGElement>, "size">;

const LABELS: Record<AIProvider, string> = {
  anthropic: "Anthropic",
  openai: "OpenAI",
  google: "Google",
  mistral: "Mistral",
  deepseek: "DeepSeek",
  perplexity: "Perplexity",
  cohere: "Cohere",
  moonshot: "Moonshot",
};

export function AIBrandLogo({ provider, size = 16, ...rest }: Props) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "currentColor",
    "aria-label": LABELS[provider],
    role: "img" as const,
    ...rest,
  };

  switch (provider) {
    case "anthropic":
      // 8-branch asterisk (Anthropic mark, simplified)
      return (
        <svg {...common}>
          <rect x="11" y="2" width="2" height="20" />
          <rect x="2" y="11" width="20" height="2" />
          <rect x="11" y="2" width="2" height="20" transform="rotate(45 12 12)" />
          <rect x="11" y="2" width="2" height="20" transform="rotate(-45 12 12)" />
        </svg>
      );
    case "openai":
      // Rosette knot (3 ellipses)
      return (
        <svg {...common} fill="none" stroke="currentColor" strokeWidth="1.6">
          <ellipse cx="12" cy="12" rx="9" ry="4" />
          <ellipse cx="12" cy="12" rx="9" ry="4" transform="rotate(60 12 12)" />
          <ellipse cx="12" cy="12" rx="9" ry="4" transform="rotate(-60 12 12)" />
        </svg>
      );
    case "google":
      // Gemini-style 4-point sparkle
      return (
        <svg {...common}>
          <path d="M12 2 L13.5 10.5 L22 12 L13.5 13.5 L12 22 L10.5 13.5 L2 12 L10.5 10.5 Z" />
        </svg>
      );
    case "mistral":
      // Geometric M (stacked bars)
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="2.4" />
          <rect x="3" y="8.5" width="18" height="2.4" />
          <rect x="3" y="13" width="18" height="2.4" />
          <rect x="3" y="17.6" width="18" height="2.4" />
          <rect x="3" y="4" width="3" height="16" />
          <rect x="18" y="4" width="3" height="16" />
          <rect x="10.5" y="8.5" width="3" height="11.5" />
        </svg>
      );
    case "deepseek":
      // Whale silhouette
      return (
        <svg {...common}>
          <path d="M2 13 C 4 9, 9 7, 14 8 C 18 8.6, 21 11, 22 13 L 20 13.5 L 21.5 16 L 18 14.5 C 16 16, 13 16.5, 10 16 C 6 15.4, 3 14.5, 2 13 Z" />
          <circle cx="17" cy="11" r="0.9" fill="var(--ivory)" />
        </svg>
      );
    case "perplexity":
      // Orb with longitudes
      return (
        <svg {...common} fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="9" />
          <ellipse cx="12" cy="12" rx="3.5" ry="9" />
          <line x1="3" y1="12" x2="21" y2="12" />
        </svg>
      );
    case "cohere":
      // Cercle bordeaux à 7 rayons — référence à la signature Cohere
      return (
        <svg {...common} fill="none" stroke="currentColor" strokeWidth="1.6">
          <circle cx="12" cy="12" r="3.2" fill="currentColor" stroke="none" />
          {Array.from({ length: 7 }).map((_, i) => {
            const angle = (i * (360 / 7) - 90) * (Math.PI / 180);
            const x1 = 12 + Math.cos(angle) * 5;
            const y1 = 12 + Math.sin(angle) * 5;
            const x2 = 12 + Math.cos(angle) * 10;
            const y2 = 12 + Math.sin(angle) * 10;
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} strokeLinecap="round" />;
          })}
        </svg>
      );
    case "moonshot":
      // Crescent moon — Moonshot / Kimi
      return (
        <svg {...common}>
          <path d="M15.5 3a9 9 0 1 0 5.5 16.1A7.5 7.5 0 0 1 15.5 3z" />
        </svg>
      );
  }
}
